import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { initialOrdemComprasListaFilters, useOrdemComprasListaFilters } from "../hooks/OrdemCompras_lista/filters";
import { useOrdemComprasListaPagination } from "../hooks/OrdemCompras_lista/pagination";
import { OrdemCompraLista } from "../../domain/models/ordem_compra_lista";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { FornecedorDropDown } from "../../../../common/domain/models/fornecedor_dropdown";
import { ProdutoServicoDropDown } from "../../../../common/domain/models/produto_servico_dropdown";
import { ColetaPrecoDropDown } from "../../../../common/domain/models/coleta_preco_dropdown";
import { OrdemCompraDropDown } from "../../../../common/domain/models/ordem_compra_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { EmailForm } from "../../../../common/presentation/schemas/email";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { emailTemplates } from "../../../../common/utils/email_templates";
import { ReportOrdemComprasForm } from "../schemas/OrdemCompras_lista/report_ordem_compras";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { ReportOrdemComprasLoteForm } from "../schemas/OrdemCompras_lista/report_ordem_compras_lote";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { TelasIds } from "../../../../common/utils/tela_ids";

/**
 * LoadOrdemComprasFuncState
 */

type LoadOrdemComprasFuncState = {
  centroCusto?: number[] | null;
  fornecedor?: number[] | null;
  coletaPreco?: number[] | null;
  ordemCompra?: number[] | null;
  produtoServico?: number[] | null;
  status?: number[] | null;
  statusNF?: number;
  nroNotaFiscal?: string | null;
  dataOCStart?: Date | null;
  dataOCEnd?: Date | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useOrdemComprasListaViewModel
 */

export const useOrdemComprasListaViewModel = () => {

  const {
    reports: {
      getReportOrdemCompraUseCase,
      getReportOrdemComprasUseCase,
      getReportOrdemComprasLoteUseCase,
    },
    compras: {
      getAllOrdemCompraUseCase,
      sendEmailOrdemCompraUseCase,
    }
  } = useDependencyInjection();

  const {
    loadFornecedoresDropDown,
    loadCentroCustoDropDown,
    loadProdutoServicosDropDown,
    loadOrdemComprasDropDown,
    loadColetaPrecoDropDown,
  } = useDropDownLoader();

  const deboundedHandlerRef = useCreateDebounceHandler(750);

  const { idUsuario } = useUserAuthStore();

  const backOrNavigate = useBackOrNavigate();

  const { pushErrorNotification, pushInfoNotification, setPageSaving } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [emailModal, setEmailModal] = useState(false);
  const [reportOrdemComprasModal, setReportOrdemComprasModal] = useState(false);
  const [reportOrdemComprasLoteModal, setReportOrdemComprasLoteModal] = useState(false);

  const [emailSendTo, setEmailSendTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailAttachment, setEmailAttachment] = useState("");
  const [emailBody, setEmailBody] = useState("");

  const [ordemCompras, setOrdemCompras] = useState<OrdemCompraLista[]>();

  const [selectedOrdemCompra, setSelectedOrdemCompra] = useState<OrdemCompraLista>();

  const [centroCustoDropdown, setCentroCustoDropdown] = useState<CentroCustoDropDown[]>();
  const [fornecedoresDropdown, setFornecedoresDropdown] = useState<FornecedorDropDown[]>();
  const [coletaPrecosDropdown, setColetaPrecosDropdown] = useState<ColetaPrecoDropDown[]>();
  const [ordemComprasDropdown, setOrdemComprasDropdown] = useState<OrdemCompraDropDown[]>();
  const [produtoServicosDropdown, setProdutoServicosDropdown] = useState<ProdutoServicoDropDown[]>();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const filters = useOrdemComprasListaFilters();
  const pagination = useOrdemComprasListaPagination();

  /**
   * Funcoes de carregamento
   */

  const loadOrdemCompras = useCallback(async (fs?: LoadOrdemComprasFuncState) => {
    try {
      const filteredCentroCusto = evaluateParamValue(fs?.centroCusto, filters.centroCusto);
      const filteredFornecedor = evaluateParamValue(fs?.fornecedor, filters.fornecedor);
      const filteredColetaPreco = evaluateParamValue(fs?.coletaPreco, filters.coletaPreco);
      const filteredOrdemCompra = evaluateParamValue(fs?.ordemCompra, filters.ordemCompra);
      const filteredProdutoServico = evaluateParamValue(fs?.produtoServico, filters.produtoServico);
      const filteredStatus = evaluateParamValue(fs?.status, filters.status);
      const filteredStatusNF = evaluateParamValue(fs?.statusNF, filters.statusNF);
      const filteredNroNotaFiscal = evaluateParamValue(fs?.nroNotaFiscal, filters.nroNotaFiscal);
      const filteredDataOCStart = evaluateParamValue(fs?.dataOCStart, filters.dataOCStart);
      const filteredDataOCEnd = evaluateParamValue(fs?.dataOCEnd, filters.dataOCEnd);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllOrdemCompraUseCase.getAllOrdemCompra(
        TelasIds.OrdemCompras_lista,
        filteredCentroCusto,
        filteredFornecedor,
        filteredColetaPreco,
        filteredOrdemCompra,
        filteredProdutoServico,
        filteredStatus,
        filteredStatusNF,
        filteredNroNotaFiscal,
        filteredDataOCStart,
        filteredDataOCEnd,
        filteredPage,
        filteredRowsPerPage,
      );

      setOrdemCompras(getAllOrdemCompraUseCase.items);
      pagination.setTotalItems(getAllOrdemCompraUseCase.totalItems);
    } catch (e: unknown) {
      setOrdemCompras(undefined);
      throw new Error('Falha ao buscar ordens de compra!');
    }
  }, [
    filters,
    pagination,
    getAllOrdemCompraUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadFornecedoresDropDown(dt => setFornecedoresDropdown(dt));

      await loadColetaPrecoDropDown(dt => setColetaPrecosDropdown(dt));

      await loadOrdemComprasDropDown(dt => setOrdemComprasDropdown(dt.map(x => ({ idOrdemCompra: x.id, descricao: x.title }))));

      await loadProdutoServicosDropDown(dt => setProdutoServicosDropdown(dt), {});

      await loadOrdemCompras();

    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadFornecedoresDropDown(dt => setFornecedoresDropdown(dt));

      await loadColetaPrecoDropDown(dt => setColetaPrecosDropdown(dt));

      await loadOrdemComprasDropDown(dt => setOrdemComprasDropdown(dt.map(x => ({ idOrdemCompra: x.id, descricao: x.title }))));

      await loadProdutoServicosDropDown(dt => setProdutoServicosDropdown(dt), {});

      await loadOrdemCompras();

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadCentroCustoDropDown,
    loadFornecedoresDropDown,
    loadColetaPrecoDropDown,
    loadOrdemComprasDropDown,
    loadProdutoServicosDropDown,
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  const handleOrdemCompraReportSubmit = useCallback(async (idOrdemCompra: number) => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportOrdemCompraUseCase
            .getReportOrdemCompra(idOrdemCompra, requiredMimeType),
      });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao gerar relatorio!'));
    } finally {
      setLoading(false);
    }
  }, [
    getReportOrdemCompraUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleOrdemCompraEditarSubmit = useCallback(async (idOrdemCompra: number, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/OrdemCompras_cadastro?id=${idOrdemCompra}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/OrdemCompras_cadastro?id=${idOrdemCompra}`, { alternativeRoute: `/OrdemCompras_lista` });
    }
  }, [
    backOrNavigate,
  ]);

  const handleEmailOpenState = useCallback((idOrdemCompra: number | null, openState: boolean) => {
    if (openState) {
      if (idOrdemCompra && ordemCompras) {
        const ordemCompra = ordemCompras.find(x => x.idOrdemCompra === idOrdemCompra);
        if (ordemCompra) {
          setEmailSendTo("");
          setEmailSubject("Ordem de Compra - " + ordemCompra.nrOrdemCompra);
          setEmailAttachment(`ORDEM_COMPRA_${ordemCompra.nrOrdemCompra}.pdf`);
          setEmailBody(emailTemplates.requisicaoCompra);
        }
        setSelectedOrdemCompra(ordemCompras.find(x => x.idOrdemCompra === idOrdemCompra));
      } else {
        setSelectedOrdemCompra(undefined);
      }
    }
    setEmailModal(openState);
  }, [
    ordemCompras,
  ]);

  const handleSendMailSubmit = useCallback(async (form: EmailForm) => {
    if (selectedOrdemCompra) {
      try {
        setSaving(true);
        setPageSaving(true);

        await sendEmailOrdemCompraUseCase
          .sendEmailOrdemCompra(
            selectedOrdemCompra.idOrdemCompra,
            form.subject,
            form.sendTo,
            form.body,
            form.attachment,
          );

        pushInfoNotification("Sucesso", "Email enviado com sucesso!");
        setEmailModal(false);

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedOrdemCompra,
    sendEmailOrdemCompraUseCase,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleReportOrdemComprasOpenState = useCallback((openState: boolean) => {
    setReportOrdemComprasModal(openState);
  }, [
  ]);

  const handleReportOrdemComprasSubmit = useCallback(async (form: ReportOrdemComprasForm) => {
    if (idUsuario) {
      try {
        setLoading(true);

        await startReportViewerProcess({
          fetchCallback: async (requiredMimeType) =>
            await getReportOrdemComprasUseCase.getReportOrdemCompras(
              requiredMimeType,
              idUsuario,
              form.ordemCompras,
              form.centroCustos,
              form.status,
              form.fornecedores,
              form.data,
            ),
          closeReportModalCallback: () => {
            setReportOrdemComprasModal(false);
          },
        });

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idUsuario,
    getReportOrdemComprasUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleReportOrdemComprasLoteOpenState = useCallback((openState: boolean) => {
    setReportOrdemComprasLoteModal(openState);
  }, [
  ]);

  const handleReportOrdemComprasLoteSubmit = useCallback(async (form: ReportOrdemComprasLoteForm) => {
    try {
      setLoading(true);

      await getReportOrdemComprasLoteUseCase.getReportOrdemComprasLote(
        form.centroCusto,
        form.fornecedores,
        form.status,
        form.competencia,
      );

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    getReportOrdemComprasLoteUseCase,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);

      useOrdemComprasListaPagination.setState({ page: page + 1 });
      await loadOrdemCompras({ page: page + 1 });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);

      useOrdemComprasListaPagination.setState({ page: 1, rowsPerPage });
      await loadOrdemCompras({ page: 1, rowsPerPage });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  const handleCentroCustoChange = useCallback(async (centroCusto: number[] | null) => {
    try {
      setLoading(true);

      useOrdemComprasListaFilters.setState({ centroCusto });
      useOrdemComprasListaPagination.setState({ page: 1 });

      await loadOrdemCompras({ page: 1, centroCusto });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  const handleFornecedorChange = useCallback(async (fornecedor: number[] | null) => {
    try {
      setLoading(true);

      useOrdemComprasListaFilters.setState({ fornecedor });
      useOrdemComprasListaPagination.setState({ page: 1 });

      await loadOrdemCompras({ page: 1, fornecedor });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  const handleColetaPrecoChange = useCallback(async (coletaPreco: number[] | null) => {
    try {
      setLoading(true);

      useOrdemComprasListaFilters.setState({ coletaPreco });
      useOrdemComprasListaPagination.setState({ page: 1 });

      await loadOrdemCompras({ page: 1, coletaPreco });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  const handleOrdemCompraChange = useCallback(async (ordemCompra: number[] | null) => {
    try {
      setLoading(true);

      useOrdemComprasListaFilters.setState({ ordemCompra });
      useOrdemComprasListaPagination.setState({ page: 1 });

      await loadOrdemCompras({ page: 1, ordemCompra });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  const handleProdutoServicoChange = useCallback(async (produtoServico: number[] | null) => {
    try {
      setLoading(true);

      useOrdemComprasListaFilters.setState({ produtoServico });
      useOrdemComprasListaPagination.setState({ page: 1 });

      await loadOrdemCompras({ page: 1, produtoServico });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  const handleStatusChange = useCallback(async (status: number[] | null) => {
    try {
      setLoading(true);

      useOrdemComprasListaFilters.setState({ status });
      useOrdemComprasListaPagination.setState({ page: 1 });

      await loadOrdemCompras({ page: 1, status });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  const handleStatusNFChange = useCallback(async (statusNF: number) => {
    try {
      setLoading(true);

      useOrdemComprasListaFilters.setState({ statusNF });
      useOrdemComprasListaPagination.setState({ page: 1 });

      await loadOrdemCompras({ page: 1, statusNF });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  const handleNroNotaFiscalChange = useDebouncedCallback(deboundedHandlerRef, async (nroNotaFiscal: string | null) => {
    useOrdemComprasListaFilters.setState({ nroNotaFiscal });
    return async () => {
      try {
        setLoading(true);

        useOrdemComprasListaFilters.setState({ nroNotaFiscal });
        useOrdemComprasListaPagination.setState({ page: 1 });

        await loadOrdemCompras({ page: 1, nroNotaFiscal });

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  const handleDataOCRChange = useCallback(async (de: Date | null, ate: Date | null) => {
    try {
      setLoading(true);

      useOrdemComprasListaFilters.setState({ dataOCStart: de, dataOCEnd: ate });
      useOrdemComprasListaPagination.setState({ page: 1 });

      await loadOrdemCompras({ page: 1, dataOCStart: de, dataOCEnd: ate });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);

      useOrdemComprasListaFilters.setState({ ...initialOrdemComprasListaFilters });
      useOrdemComprasListaPagination.setState({ page: 1 });

      await loadOrdemCompras({ page: 1, ...initialOrdemComprasListaFilters });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOrdemCompras,
    pushErrorNotification,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    emailModal,
    reportOrdemComprasModal,
    reportOrdemComprasLoteModal,
    emailSendTo,
    emailSubject,
    emailAttachment,
    emailBody,
    ordemCompras: ordemCompras ?? [],
    selectedOrdemCompra: selectedOrdemCompra ?? null,
    centroCustoDropdown: centroCustoDropdown ?? [],
    fornecedoresDropdown: fornecedoresDropdown ?? [],
    coletaPrecosDropdown: coletaPrecosDropdown ?? [],
    ordemComprasDropdown: ordemComprasDropdown ?? [],
    produtoServicosDropdown: produtoServicosDropdown ?? [],
    reportViewerState,
    filters,
    pagination,
    handleReload,
    handleOrdemCompraReportSubmit,
    handleOrdemCompraEditarSubmit,
    handleEmailOpenState,
    handleSendMailSubmit,
    handleReportOrdemComprasOpenState,
    handleReportOrdemComprasSubmit,
    handleReportOrdemComprasLoteOpenState,
    handleReportOrdemComprasLoteSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleCentroCustoChange,
    handleFornecedorChange,
    handleColetaPrecoChange,
    handleOrdemCompraChange,
    handleProdutoServicoChange,
    handleStatusChange,
    handleStatusNFChange,
    handleNroNotaFiscalChange,
    handleDataOCRChange,
    handleClearFilters,
  };
}